import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { categoriaDoAnexo, extensaoDe } from '@mymaxx/shared';
import { useEffect, useState } from 'react';
/**
 * A espiada no conteúdo, dentro do cartão do arquivo.
 *
 * Lida no próprio navegador, com o arquivo que a página já pode buscar (decisão de 08/10):
 * nada é enviado a lugar nenhum e nenhum container novo entra em pé. As bibliotecas só são
 * baixadas quando aparece um arquivo daquele tipo — quem nunca recebe PDF não paga por elas.
 *
 * Falha calada de propósito: prévia é conforto, não função. PDF corrompido, planilha com
 * formato estranho ou rede ruim simplesmente não mostram nada, e o cartão com nome, tipo,
 * tamanho e os dois botões continua inteiro.
 */
/**
 * Onde está o worker do pdf.js.
 *
 * Quem informa é a aplicação, não este pacote: o caminho do worker só existe depois do
 * empacotamento, e a sintaxe que o resolve (`?url`) é do Vite — `@mymaxx/ui` compila com
 * `tsc` puro e não a entende. Deixar a aplicação dizer mantém o pacote portátil.
 *
 * Vazio = sem prévia de PDF, e o cartão segue inteiro. O worker vem do próprio pacote
 * instalado, nunca de CDN: buscar de domínio de terceiro seria mandar o navegador do
 * atendente a outro lugar para ler documento de cliente.
 */
let workerDePdf = '';
export function definirWorkerDePdf(url) {
    workerDePdf = url;
}
/** Quanto do arquivo vale baixar para espiar. Acima disso, só o cartão. */
const LIMITE_PREVIA_BYTES = 8 * 1024 * 1024;
/** Quantas linhas da planilha cabem no cartão sem ele virar a tela toda. */
const LINHAS_DA_PREVIA = 5;
const COLUNAS_DA_PREVIA = 4;
export function PreviaDoArquivo({ url, mimeType, nomeOriginal, previaUrl, }) {
    // Com prévia pronta do servidor, é ela que se desenha — e como ela é um PDF de uma
    // página, o caminho é exatamente o mesmo do PDF enviado pela pessoa. Apresentação e
    // Word entram por aqui sem nenhum código próprio.
    const categoria = previaUrl ? 'pdf' : categoriaDoAnexo(mimeType, nomeOriginal);
    const deOndeLer = previaUrl ?? url;
    const [miniatura, setMiniatura] = useState(null);
    const [tabela, setTabela] = useState(null);
    const [paginas, setPaginas] = useState(null);
    useEffect(() => {
        if (categoria !== 'pdf' && categoria !== 'planilha')
            return;
        let valeu = true;
        void (async () => {
            try {
                const resposta = await fetch(deOndeLer);
                if (!resposta.ok)
                    return;
                const tamanho = Number(resposta.headers.get('content-length') ?? 0);
                if (tamanho > LIMITE_PREVIA_BYTES)
                    return;
                const dados = await resposta.arrayBuffer();
                if (!valeu)
                    return;
                if (categoria === 'pdf') {
                    const { imagem, total } = await primeiraPaginaDoPdf(dados);
                    if (!valeu)
                        return;
                    setMiniatura(imagem);
                    // Com prévia do servidor, o PDF tem uma página só — e "1 página" diria algo sobre
                    // a conversão, não sobre o documento. Melhor não dizer nada.
                    setPaginas(previaUrl ? null : total);
                    return;
                }
                const linhas = await primeirasLinhasDaPlanilha(dados, nomeOriginal);
                if (valeu)
                    setTabela(linhas);
            }
            catch {
                // Prévia é conforto: o cartão já diz o que o arquivo é e como abri-lo.
            }
        })();
        return () => {
            valeu = false;
        };
    }, [deOndeLer, categoria, nomeOriginal]);
    if (miniatura) {
        return (_jsxs("div", { className: "relative border-b border-black/10 bg-white dark:border-white/10", children: [_jsx("img", { src: miniatura, alt: "", className: "max-h-40 w-full object-cover object-top" }), paginas !== null && paginas > 1 && (_jsxs("span", { className: "absolute right-1 bottom-1 rounded bg-black/60 px-1.5 text-[10px] text-white", children: [paginas, " p\u00E1ginas"] }))] }));
    }
    if (tabela && tabela.length > 0) {
        return (_jsx("div", { className: "overflow-hidden border-b border-black/10 bg-white/60 dark:border-white/10 dark:bg-black/20", children: _jsx("table", { className: "w-full table-fixed text-[10px]", children: _jsx("tbody", { children: tabela.map((linha, i) => (_jsx("tr", { className: i === 0 ? 'font-medium' : '', children: linha.map((celula, j) => (_jsx("td", { className: "truncate border-b border-black/5 px-1.5 py-0.5", children: celula }, j))) }, i))) }) }) }));
    }
    return null;
}
/**
 * A primeira página do PDF, desenhada num canvas.
 *
 * `import()` tardio: o pdf.js tem alguns megabytes, e a maioria das conversas nunca vê um
 * PDF. Carregá-lo no arranque faria todo mundo pagar pelo que poucos usam.
 */
async function primeiraPaginaDoPdf(dados) {
    if (!workerDePdf)
        return { imagem: null, total: 0 };
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = workerDePdf;
    const documento = await pdfjs.getDocument({ data: dados }).promise;
    const pagina = await documento.getPage(1);
    const escala = 1.2;
    const vista = pagina.getViewport({ scale: escala });
    const canvas = document.createElement('canvas');
    canvas.width = Math.min(600, Math.ceil(vista.width));
    canvas.height = Math.ceil((canvas.width / vista.width) * vista.height);
    const contexto = canvas.getContext('2d');
    if (!contexto)
        return { imagem: null, total: documento.numPages };
    const ajustada = pagina.getViewport({ scale: (canvas.width / vista.width) * escala });
    await pagina.render({ canvas, canvasContext: contexto, viewport: ajustada }).promise;
    return { imagem: canvas.toDataURL('image/png'), total: documento.numPages };
}
/**
 * As primeiras linhas da planilha.
 *
 * CSV é lido aqui mesmo, sem biblioteca: é texto separado por vírgula ou ponto e vírgula, e
 * é o formato que mais sai de ERP e de relógio de ponto. O resto vai para o exceljs, que
 * também só é baixado quando aparece uma planilha.
 */
async function primeirasLinhasDaPlanilha(dados, nome) {
    if (extensaoDe(nome) === 'csv')
        return primeirasLinhasDoCsv(dados);
    const ExcelJS = await import('exceljs');
    const pasta = new ExcelJS.Workbook();
    await pasta.xlsx.load(dados);
    const aba = pasta.worksheets[0];
    if (!aba)
        return null;
    const linhas = [];
    aba.eachRow((linha, numero) => {
        if (numero > LINHAS_DA_PREVIA)
            return;
        const celulas = [];
        for (let c = 1; c <= COLUNAS_DA_PREVIA; c++) {
            const valor = linha.getCell(c).value;
            celulas.push(valor === null || valor === undefined ? '' : String(textoDaCelula(valor)));
        }
        linhas.push(celulas);
    });
    return linhas;
}
/** O que mostrar de uma célula: fórmula vira o resultado, data vira data curta. */
function textoDaCelula(valor) {
    if (valor instanceof Date)
        return valor.toLocaleDateString('pt-BR');
    if (typeof valor === 'object' && valor !== null) {
        const objeto = valor;
        if (objeto.richText)
            return objeto.richText.map((p) => p.text).join('');
        if (objeto.result !== undefined)
            return String(objeto.result);
        if (objeto.text !== undefined)
            return String(objeto.text);
        return '';
    }
    return String(valor);
}
function primeirasLinhasDoCsv(dados) {
    const texto = new TextDecoder('utf-8').decode(dados.slice(0, 64 * 1024));
    // Ponto e vírgula primeiro: é o separador do Excel em português, e é o que sai do
    // HubSoft. Vírgula só quando não houver ponto e vírgula na primeira linha.
    const primeira = texto.split(/\r?\n/)[0] ?? '';
    const separador = primeira.includes(';') ? ';' : ',';
    return texto
        .split(/\r?\n/)
        .slice(0, LINHAS_DA_PREVIA)
        .filter((l) => l.trim())
        .map((l) => l.split(separador).slice(0, COLUNAS_DA_PREVIA));
}
//# sourceMappingURL=PreviaDoArquivo.js.map